import { useState, useEffect } from "react";
import { basePackingItems } from "../data/basePackingItems"; // ✅ import extras list
import {
  SwipeableList,
  SwipeableListItem,
  TrailingActions,
  SwipeAction,
} from "react-swipeable-list";
import "react-swipeable-list/dist/styles.css";


// ✅ Define all kid age groups in one place
const KID_AGE_GROUPS = [
  { key: "0-12M", label: "0 - 12 months" },
  { key: "1-3Y", label: "1 - 3 years" },
  { key: "3-5Y", label: "3 - 5 years" },
  { key: "5-12Y", label: "5 - 12 years" },
  { key: "12-18Y", label: "12 - 18 years" },
];

// ✅ Default counts object helper
const defaultKidsCounts = () =>
  KID_AGE_GROUPS.reduce((acc, g) => ({ ...acc, [g.key]: 0 }), {});

export default function PackingForm({ onGenerate, onProfileChange }) {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [destination, setDestination] = useState("");
  const [days, setDays] = useState("");
  const [extras, setExtras] = useState([]);
  const [customItems, setCustomItems] = useState([]);
  const [customInput, setCustomInput] = useState("");

  // ✅ Kids: toggle + counts per age group (supports multiple groups)
  const [travelingWithKids, setTravelingWithKids] = useState(false);
  const [kidsCounts, setKidsCounts] = useState(defaultKidsCounts());

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("profiles")) || [];
    setProfiles(saved);

    // Auto-select first profile if available
    if (saved.length > 0) {
      setSelectedProfileId(saved[0].id);

      // Load that profile's trip details if any
      const prefs = saved[0].preferences || {};
      if (prefs.destination) setDestination(prefs.destination);
      if (prefs.days) setDays(prefs.days);
      if (prefs.extras) setExtras(prefs.extras);
      if (prefs.customItems) setCustomItems(prefs.customItems);
      if (prefs.travelingWithKids !== undefined)
        setTravelingWithKids(prefs.travelingWithKids);
      if (prefs.kidsCounts)
        setKidsCounts({ ...defaultKidsCounts(), ...prefs.kidsCounts });
    }
  }, []);

  // Whenever the selected profile changes, load its saved preferences
  useEffect(() => {
    if (!selectedProfileId) return;

    const saved = JSON.parse(localStorage.getItem("profiles")) || [];
    const selectedProfile = saved.find((p) => p.id === selectedProfileId);

    if (selectedProfile && selectedProfile.preferences) {
      const prefs = selectedProfile.preferences;
      if (prefs.destination !== undefined) setDestination(prefs.destination);
      if (prefs.days !== undefined) setDays(prefs.days);
      if (prefs.extras !== undefined) setExtras(prefs.extras);
      if (prefs.customItems !== undefined) setCustomItems(prefs.customItems);
      if (prefs.travelingWithKids !== undefined)
        setTravelingWithKids(prefs.travelingWithKids);
      if (prefs.kidsCounts !== undefined)
        setKidsCounts({ ...defaultKidsCounts(), ...prefs.kidsCounts });
      else setKidsCounts(defaultKidsCounts());
    } else {
      // reset if profile has no saved prefs
      setDestination("");
      setDays("");
      setExtras([]);
      setCustomItems([]);
      setTravelingWithKids(false);
      setKidsCounts(defaultKidsCounts());
    }
  }, [selectedProfileId]);

  const toggleExtra = (extra) => {
    setExtras((prev) =>
      prev.includes(extra) ? prev.filter((e) => e !== extra) : [...prev, extra]
    );
  };

  const addCustomItem = () => {
    if (customInput.trim() !== "") {
      setCustomItems([...customItems, customInput.trim()]);
      setCustomInput("");
    }
  };

  const removeCustomItem = (index) => {
    setCustomItems(customItems.filter((_, i) => i !== index));
  };

  const handleKidCountChange = (groupKey, value) => {
    const intVal = parseInt(value, 10);
    setKidsCounts((prev) => ({ ...prev, [groupKey]: isNaN(intVal) ? 0 : intVal }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const selectedProfile = profiles.find((p) => p.id === selectedProfileId);
    if (!selectedProfile) {
      alert("Please select a traveler profile first!");
      return;
    }

    // ✅ Save trip details to profile, with kids info
    const updatedProfiles = profiles.map((p) =>
      p.id === selectedProfile.id
        ? {
            ...p,
            preferences: {
              destination,
              days,
              extras,
              customItems,
              travelingWithKids,
              kidsCounts, // <-- stores counts per age group
            },
          }
        : p
    );

    setProfiles(updatedProfiles);
    localStorage.setItem("profiles", JSON.stringify(updatedProfiles));

    // ✅ Generate the list, passing kids info
    onGenerate({
      destination,
      days,
      traveler: selectedProfile.type,
      gender: selectedProfile.gender,
      extras,
      customItems,
      profileName: selectedProfile.name,
      travelingWithKids,
      kidsCounts, // <-- available for multiplying by days later
    });
  };

  return (
    <form className="card" onSubmit={handleSubmit}>
      <h2 className="app-heading">Trip Details</h2>

      {/* Select Traveler Profile */}
      <div className="card-section">
        <label>
          Traveler Profile:
          <select
            value={selectedProfileId}
            onChange={(e) => {
              const newId = e.target.value;
              setSelectedProfileId(newId);

              const saved = JSON.parse(localStorage.getItem("profiles")) || [];
              const selected = saved.find((p) => p.id === newId);
              if (selected && onProfileChange) {
                onProfileChange(selected); // ✅ tell App which profile is active
              }
            }}
          >
            <option value="">-- Select a profile --</option>
            {profiles.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Destination + Days */}
      <div className="card-section">
        <input
          type="text"
          placeholder="Destination"
          value={destination}
          onChange={(e) => setDestination(e.target.value)}
          required
        />
        <input
          type="number"
          min="1"
          placeholder="# of Days"
          value={days}
          onChange={(e) => setDays(e.target.value)}
          required
        />
      </div>

      {/* ✅ Traveling with Kids toggle (consistent with Extras styling) */}
      <div className="card-section">
        <label className="switch">
          <input
            type="checkbox"
            checked={travelingWithKids}
            onChange={() => setTravelingWithKids(!travelingWithKids)}
          />
          <span className="slider"></span>
          <span className="switch-label">Traveling with Kids?</span>
        </label>
      </div>

      {/* ✅ Multiple age groups with inline # of kids selectors */}
      {travelingWithKids && (
        <fieldset className="card-section" style={{ border: "1px solid #eee", borderRadius: 10 }}>
          <legend>Kids by Age Group</legend>
          {KID_AGE_GROUPS.map((g) => (
            <div
              key={g.key}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 0",
                borderBottom: "1px solid #f2f2f2",
              }}
            >
              <span style={{ fontSize: 15 }}>{g.label}</span>
              <select
                value={kidsCounts[g.key] ?? 0}
                onChange={(e) => handleKidCountChange(g.key, e.target.value)}
                style={{ width: 80 }}
              >
                {[0, 1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </fieldset>
      )}

      {/* Extras (dynamic from basePackingItems.js) */}
      <fieldset className="card-section extras-group">
        <legend>Extras</legend>
        {Object.keys(basePackingItems.extras).map((extraKey) => (
          <label key={extraKey} className="switch">
            <input
              type="checkbox"
              checked={extras.includes(extraKey)}
              onChange={() => toggleExtra(extraKey)}
            />
            <span className="slider"></span>
            <span className="switch-label">
              {extraKey
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (s) => s.toUpperCase())}
            </span>
          </label>
        ))}
      </fieldset>

      {/* Custom Items */}
      <div className="card-section">
        <label>Add Custom Items:</label>
        <div className="custom-input">
          <input
            type="text"
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            placeholder="e.g. GoPro"
          />
          <button type="button" className="tertiary-btn" onClick={addCustomItem}>
            Add
          </button>
        </div>

        {/* ✅ Swipeable Custom Items */}
        <SwipeableList>
          {customItems.map((item, idx) => {
            const trailingActions = (
              <TrailingActions>
                <SwipeAction destructive={true} onClick={() => removeCustomItem(idx)}>
                  <div className="swipe-delete">Delete</div>
                </SwipeAction>
              </TrailingActions>
            );

            return (
              <SwipeableListItem key={idx} trailingActions={trailingActions}>
                <div className="custom-item">
                  {item}
                  <button
                    type="button"
                    className="delete-btn"
                    onClick={() => removeCustomItem(idx)}
                  >
                    Delete
                  </button>
                </div>
              </SwipeableListItem>
            );
          })}
        </SwipeableList>
      </div>

      
      {/* Submit Button */}
      <button type="submit" className="primary-btn">Generate List</button>
    </form>
  );
}
